// src/pages/MedicationReconciliation/MedicationReconciliationPage.js
import React, { useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Tooltip,
  Snackbar,
  Alert,
  LinearProgress,
  Chip,
  Grid,
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import { useReconciliation } from './MedicationReconciliationComp/useReconciliation';
import {
  PrescriptionEntry,
  MedsActiveTable,
  MedsExternalTable,
  ReconciliationBoard,
  SummaryCard,
  ConfirmDialog,
  FiltersBar,
  PatientDataGrid,
  PatientInfoCard,
} from './MedicationReconciliationComp';

const sectionHeaderSx = {
  backgroundColor: '#607d8b',
  color: '#fff',
  px: 2,
  py: 1,
  borderRadius: '8px 8px 0 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

export default function MedicationReconciliationPage() {
  const {
    // Data
    patients,
    selectedPatient,
    selectedPatientId,
    activeMeds,
    externalMeds,
    reconciliationItems,

    // UI
    loadingPatient,
    loadingPatients,
    loadingAI,
    message,
    notes,
    summary,

    // Actions
    setNotes,
    selectPatient,
    generateAISuggestions,
    updateItemAction,
    updateItemTarget,
    setItemNote,
    setItemReason,
    addExternalPrescription,
    confirmReconciliation,
    reset,

    // Pagination & filters
    total,
    filters,
    applyFilters,
    paginationModel,
    handlePaginationModelChange,

    // AdvancedMD
    advancedMdMatch,
  } = useReconciliation();

  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <Box p={2}>
      {(loadingAI || loadingPatient || loadingPatients) && <LinearProgress />}

      {/* --- Vista 1: DataGrid with patient list --- */}
      {!selectedPatient && (
        <>
          <FiltersBar filters={filters} onApplyFilters={applyFilters} />
          <PatientDataGrid
            rows={patients}
            loading={loadingPatients}
            rowCount={total}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            onSelectPatient={selectPatient}
          />
        </>
      )}

      {/* --- Vista 2: Patient selected — all sections visible --- */}
      {selectedPatient && (
        <Stack spacing={3}>
          <PatientInfoCard
            patient={selectedPatient}
            advancedMdMatch={advancedMdMatch}
            onBack={reset}
          />

          {/* ── Section 1: Medications Overview ── */}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={sectionHeaderSx}>
              <Typography variant="subtitle1" fontWeight={600}>
                Medications Overview
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                {/* Active Medications */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    Active Medications [{activeMeds.length}]
                  </Typography>
                  <MedsActiveTable items={activeMeds} loading={loadingPatient} />
                </Grid>
                {/* External Medications */}
                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      External Medications [{externalMeds.length}]
                    </Typography>
                    <PrescriptionEntry
                      disabled={!selectedPatientId}
                      onAdd={addExternalPrescription}
                    />
                    <MedsExternalTable items={externalMeds} loading={loadingPatient} />
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Box>

          {/* ── Section 2: Reconciliation Board ── */}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={sectionHeaderSx}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1" fontWeight={600}>
                  Reconciliation Board
                </Typography>
                <Chip size="small" label={`Continue: ${summary.CONTINUE}`} color="success" sx={{ color: '#fff' }} />
                <Chip size="small" label={`Modify: ${summary.MODIFY}`} color="warning" sx={{ color: '#fff' }} />
                <Chip size="small" label={`Stop: ${summary.STOP}`} color="error" sx={{ color: '#fff' }} />
                <Chip size="small" label={`New: ${summary.NEW}`} color="info" sx={{ color: '#fff' }} />
              </Stack>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Generate AI suggestions and apply to the board">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ScienceIcon />}
                      disabled={!selectedPatientId || loadingAI}
                      onClick={generateAISuggestions}
                      sx={{ color: '#fff', borderColor: '#fff', '&:hover': { borderColor: '#e0e0e0', backgroundColor: 'rgba(255,255,255,0.1)' } }}
                    >
                      AI Suggestions
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!selectedPatientId || reconciliationItems.length === 0}
                  onClick={() => setConfirmOpen(true)}
                  sx={{ backgroundColor: '#fff', color: '#607d8b', '&:hover': { backgroundColor: '#e0e0e0' } }}
                >
                  Confirm Reconciliation
                </Button>
              </Stack>
            </Box>
            <Box sx={{ p: 2 }}>
              {reconciliationItems.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No items to reconcile. Add external medications or use AI Suggestions to populate the board.
                </Alert>
              )}
              <ReconciliationBoard
                items={reconciliationItems}
                loading={loadingPatient}
                onChangeAction={updateItemAction}
                onChangeTarget={updateItemTarget}
                onChangeNote={setItemNote}
                onChangeReason={setItemReason}
              />
            </Box>
          </Box>

          {/* ── Section 3: Summary & Notes ── */}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={sectionHeaderSx}>
              <Typography variant="subtitle1" fontWeight={600}>
                Summary &amp; Notes
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <SummaryCard
                summary={summary}
                notes={notes}
                onNotesChange={setNotes}
                loading={loadingPatient}
              />
            </Box>
          </Box>
        </Stack>
      )}

      {/* --- Confirmation dialog --- */}
      <ConfirmDialog
        open={confirmOpen}
        patientName={selectedPatient?.name}
        onConfirm={async () => {
          await confirmReconciliation();
          setConfirmOpen(false);
        }}
        onClose={() => setConfirmOpen(false)}
      />

      {/* --- Global snackbar --- */}
      <Snackbar
        open={!!message}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {message ? (
          <Alert severity={message.type} variant="filled">
            {message.text}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  );
}
